"use client"

import { useEffect, useRef } from "react"
import { gsap } from "gsap" // Import GSAP
import { FaChartLine, FaUserShield, FaMobileAlt, FaLightbulb } from "react-icons/fa"
import HomeNavbar from "./HomeNavbar"
import Footer from "./Footer"

const servicesData = [
  {
    id: 1,
    icon: <FaChartLine className="text-blue-500 text-4xl" />,
    title: "Real-Time Copy Trading",
    description: "Seamlessly copy trades from master accounts to your child accounts in real-time with no delays.",
  },
  {
    id: 2,
    icon: <FaUserShield className="text-blue-500 text-4xl" />,
    title: "Account Security",
    description: "Our platform ensures your account credentials are encrypted and securely managed.",
  },
  {
    id: 3,
    icon: <FaMobileAlt className="text-blue-500 text-4xl" />,
    title: "Mobile Friendly",
    description: "Trade on-the-go with our fully responsive design compatible with mobile devices.",
  },
  {
    id: 4,
    icon: <FaLightbulb className="text-blue-500 text-4xl" />,
    title: "Smart Trade Insights",
    description: "Get actionable trade insights and analysis to make informed decisions every time.",
  },
];

const Services = () => {
  const servicesRef = useRef(null);

  useEffect(() => {
    const servicesElement = servicesRef.current; // Capture the ref value
    const serviceCards = servicesElement?.querySelectorAll(".service-card") || [];
    gsap.set(serviceCards, { y: 50, opacity: 0 });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            gsap.to(serviceCards, {
              y: 0,
              opacity: 1,
              duration: 0.8,
              stagger: 0.2,
              ease: "power3.out",
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.3 }
    );

    if (servicesElement) observer.observe(servicesElement);

    return () => {
      if (servicesElement) observer.unobserve(servicesElement); // Use captured value in cleanup
    };
  }, []);

  return (
    <div className="min-h-screen bg-slate-900 text-white overflow-hidden">
      <HomeNavbar />
      <section
        ref={servicesRef}
        id="services"
        className="relative z-10 py-16 px-4 md:px-6 lg:px-8 bg-slate-800/50 backdrop-blur-sm"
      >
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Our <span className="text-blue-500">Services</span>
          </h2>
          <p className="text-xl text-slate-300 mb-12">
            We provide cutting-edge copy trading solutions to ensure you never miss a profitable trade.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {servicesData.map((service) => (
              <div
                key={service.id}
                className="service-card bg-slate-700/50 backdrop-blur-sm rounded-lg p-8 hover:bg-slate-700/80 transition-colors"
              >
                <div className="flex justify-center mb-4">{service.icon}</div>
                <h3 className="text-xl font-semibold mb-2">{service.title}</h3>
                <p className="text-slate-300">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default Services;